<template>
  <div class="content-wrapper">
    <app-page-top-section :title="$t('clients')">
      <client-action-button @open-modal="openModal" />
    </app-page-top-section>

    <!-- Search & Filter -->
    <div class="mb-3 d-flex gap-2 flex-wrap">
      <input
        type="text"
        v-model="search"
        @input="fetchClients"
        placeholder="Search by name, email, or phone"
        class="form-control"
      />

      <select v-model="filterProject" @change="fetchClients" class="form-select">
        <option value="">All Projects</option>
        <option v-for="project in projects" :key="project.id" :value="project.id">
          {{ project.title }}
        </option>
      </select>

      <select v-model="filterCity" @change="fetchClients" class="form-select">
        <option value="">All Cities</option>
        <option v-for="city in bangladeshDistricts" :key="city" :value="city">
          {{ city }}
        </option>
      </select>

      <select v-model="filterSubscription" @change="fetchClients" class="form-select">
        <option value="">All Subscription Types</option>
        <option value="monthly">Monthly</option>
        <option value="yearly">Yearly</option>
        <option value="one-time">One-time</option>
      </select>
    </div>

    <div class="mb-3 d-flex justify-content-end">
      <button class="btn btn-info" @click="openBulkSmsModal">Send SMS (All Clients)</button>
    </div>

    <!-- Clients Table -->
    <table class="table table-bordered">
      <thead>
        <tr>
          <th>{{ $t('id') }}</th>
          <th>{{ $t('name') }}</th>
          <th>{{ $t('phone') }}</th>
          <th>{{ $t('email') }}</th>
          <th>{{ $t('city') }}</th>
          <th>{{ $t('address') }}</th>
          <th>{{ $t('project') }}</th>
          <th>{{ $t('subscription_type') }}</th>
          <th>{{ $t('fee') }}</th>
          <th>{{ $t('start_date') }}</th>
          <th>{{ $t('payment_due') }}</th>
          <th>{{ $t('due_amount') }}</th>
          <th>{{ $t('actions') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(client,index) in clients.data" :key="client.id">
          <td>{{ (clients.current_page - 1) * 10 + index + 1 }}</td>
          <td>{{ client.name }}</td>
          <td>{{ client.phone }}</td>
          <td>{{ client.email }}</td>
          <td>{{ client.city }}</td>
          <td>{{ client.address }}</td>
          <td>{{ client.project ? client.project.title : '-' }}</td>
          <td>{{ client.subscription_type }}</td>
          <td>{{ client.fee }}</td>
          <td>{{ formatDate(client.start_date) }}</td>
          <td>
            <span v-if="client.payment_due === 'Payment Due'" class="badge bg-danger">{{ $t('payment_due') }}</span>
            <span v-else-if="client.payment_due === 'No Due'" class="badge bg-success">{{ $t('no_due') }}</span>
            <span v-else>-</span>
          </td>
          <td>{{ client.due_amount !== undefined ? client.due_amount : '0.00' }}</td>
          <td>
            <button class="btn btn-sm btn-primary" @click="editClient(client)">Edit</button>
            <button class="btn btn-sm btn-danger" @click="deleteClient(client.id)">Delete</button>
            <button class="btn btn-sm btn-info" @click="openSmsModal(client)" :disabled="!client.phone">Send SMS</button>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Custom Pagination -->
    <nav v-if="clients.last_page > 1">
      <ul class="pagination justify-content-center">
        <li
          class="page-item"
          :class="{ disabled: clients.current_page === 1 }"
          @click="changePage(clients.current_page - 1)"
        >
          <a class="page-link" href="#">Previous</a>
        </li>

        <li
          class="page-item"
          v-for="page in totalPages"
          :key="page"
          :class="{ active: clients.current_page === page }"
          @click="changePage(page)"
        >
          <a class="page-link" href="#">{{ page }}</a>
        </li>

        <li
          class="page-item"
          :class="{ disabled: clients.current_page === clients.last_page }"
          @click="changePage(clients.current_page + 1)"
        >
          <a class="page-link" href="#">Next</a>
        </li>
      </ul>
    </nav>

    <!-- Modal -->
    <client-form-modal
      v-if="isModalActive"
      v-model="isModalActive"
      :client="selectedClient"
      @saved="fetchClients"
    />

    <!-- SMS Modal -->
    <send-sms-modal
      v-if="isSmsModalActive"
      v-model="isSmsModalActive"
      :client="selectedSmsClient"
      @sent="handleSmsSent"
    />

    <!-- Bulk SMS Modal -->
    <bulk-sms-modal
      v-if="isBulkSmsModalActive"
      v-model="isBulkSmsModalActive"
      @sent="handleBulkSmsSent"
    />
  </div>
</template>

<script>
import ClientActionButton from './Components/ClientActionButton.vue'
import ClientFormModal from './Components/ClientFormModal.vue'
import SendSmsModal from './Components/SendSmsModal.vue'
import BulkSmsModal from './Components/BulkSmsModal.vue'

export default {
  name: 'AppClients',
  components: { ClientActionButton, ClientFormModal, SendSmsModal, BulkSmsModal },
  data() {
    return {
      clients: { data: [], current_page: 1, last_page: 1 },
      projects: [],
      isModalActive: false,
      selectedClient: null,
      isSmsModalActive: false,
      selectedSmsClient: null,
      isBulkSmsModalActive: false,
      search: '',
      filterProject: '',
      filterCity: '',
      filterSubscription: '',
      bangladeshDistricts: [
        'Dhaka', 'Gazipur', 'Narayanganj', 'Tangail', 'Kishoreganj', 'Manikganj', 'Munshiganj', 'Narsingdi', 'Rajbari', 'Shariatpur', 'Faridpur', 'Gopalganj', 'Madaripur',
        'Chittagong', 'Cox\'s Bazar', 'Bandarban', 'Khagrachhari', 'Rangamati', 'Comilla', 'Feni', 'Brahmanbaria', 'Chandpur', 'Lakshmipur', 'Noakhali',
        'Sylhet', 'Moulvibazar', 'Habiganj', 'Sunamganj',
        'Rajshahi', 'Bogra', 'Joypurhat', 'Naogaon', 'Natore', 'Chapainawabganj', 'Pabna', 'Sirajganj',
        'Rangpur', 'Dinajpur', 'Gaibandha', 'Kurigram', 'Lalmonirhat', 'Nilphamari', 'Panchagarh', 'Thakurgaon',
        'Khulna', 'Bagerhat', 'Chuadanga', 'Jashore', 'Jhenaidah', 'Kushtia', 'Magura', 'Meherpur', 'Narail', 'Satkhira',
        'Barisal', 'Barguna', 'Bhola', 'Jhalokati', 'Patuakhali', 'Pirojpur',
        'Mymensingh', 'Jamalpur', 'Netrokona', 'Sherpur'
      ]
    }
  },
  mounted() {
    this.fetchProjects()
    this.fetchClients()
  },
  computed: {
    totalPages() {
      let pages = []
      for (let i = 1; i <= this.clients.last_page; i++) {
        pages.push(i)
      }
      return pages
    }
  },
  methods: {
    fetchProjects() {
      axios.get('/clients/projects')
        .then(res => { this.projects = res.data })
        .catch(err => console.error(err))
    },
    fetchClients(page = 1) {
      axios.get('/clients', {
        params: {
          search: this.search,
          project_id: this.filterProject,
          city: this.filterCity,
          subscription_type: this.filterSubscription,
          per_page: 10,
          page: page
        }
      }).then(res => { this.clients = res.data })
    },
    changePage(page) {
      if (page < 1 || page > this.clients.last_page) return
      this.fetchClients(page)
    },
    openModal() {
      this.selectedClient = null
      this.isModalActive = true
    },
    editClient(client) {
      this.selectedClient = client
      this.isModalActive = true
    },
    deleteClient(id) {
      Swal.fire({
        title: 'Are you sure?',
        text: 'This will permanently delete this client.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!'
      }).then((result) => {
        if (result.isConfirmed) {
          axios.delete(`/clients/${id}`).then(() => {
            this.fetchClients(this.clients.current_page)
            Swal.fire({
              title: 'Deleted!',
              text: 'Client record has been deleted.',
              icon: 'success',
              confirmButtonColor: '#3085d6'
            })
          })
        }
      })
    },
    formatDate(date) {
      if (!date) return '-'
      const d = new Date(date)
      const day = String(d.getDate()).padStart(2, '0')
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const year = d.getFullYear()
      return `${day}-${month}-${year}`
    },
    openSmsModal(client) {
      if (!client.phone) {
        Swal.fire({
          title: 'Missing Phone Number!',
          text: 'Client does not have a phone number configured.',
          icon: 'warning',
          confirmButtonColor: '#3085d6'
        })
        return
      }
      this.selectedSmsClient = client
      this.isSmsModalActive = true
    },
    handleSmsSent() {
      this.isSmsModalActive = false
      this.selectedSmsClient = null
    },
    openBulkSmsModal() {
      this.isBulkSmsModalActive = true
    },
    handleBulkSmsSent() {
      this.isBulkSmsModalActive = false
    }
  }
}
</script>

<style>
.pagination .page-item.disabled .page-link {
  pointer-events: none;
  opacity: 0.5;
}
.pagination .page-item.active .page-link {
  background-color: #0d6efd;
  border-color: #0d6efd;
  color: #fff;
}
</style>

